/*
 *   Copyright (c) 2021 Propally.Inc
 *   All rights reserved.
 */

import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { atomFamily, selectorFamily, useRecoilCallback, atom, useRecoilValue, useSetRecoilState } from "recoil";

import {
  User,
  Property,
  Image,
  LeaseContract,
  MonthlyCashflowResult,
  SimulationConfigWithId,
  RegisteredStatusEnum,
  UserRegistrations,
  JobCategory,
} from "../api/generated/api";
import { PROPERTY_TYPE_VALUES } from "../api/mappings/property";
import { DEFAULT_ACCOUNT_REGISTER_FORM_SCHEMA } from "../pages/renewal_v1/account/const";
import { AccountRegisterFormSchema } from "../pages/renewal_v1/account/types";
import { DEFAULT_ADD_NEW_PROPERTY_FORM_SCHEMA } from "../pages/renewal_v1/management/add_new_property/const";
import { AddNewPropertyFormSchema } from "../pages/renewal_v1/management/add_new_property/type";
import {
  setStorageAccountRegistrationState,
  setStorageConsiderationPropertyRegistrationState,
  setStoragePropertyRegistrationState,
} from "../utilities/storage";

import { Properties } from "./property_selectors";

export type Session = {
  logged_in: boolean;
};

export const SessionState = atom<Session>({
  key: "SessionState",
  default: {
    logged_in: false,
  },
});

// conditional flag for React.useEffect() on initial loading.
export const RequireLoadingFromBackendState = atom<number>({
  key: "require_loading_from_backend",
  default: 0,
});

export const useRequestInitialLoading = () => {
  const update_atom = useRecoilCallback(({ snapshot, set }) => async () => {
    let require_loading = await snapshot.getPromise(RequireLoadingFromBackendState);
    set(RequireLoadingFromBackendState, ++require_loading);
  });
  return update_atom;
};

export const UserState = atom<User>({
  key: "UserState",
  default: null,
});

export const UserDataFirstRegisteredStatusState = atom<RegisteredStatusEnum>({
  key: "UserDataFirstRegisteredStatusState",
  default: RegisteredStatusEnum.Registration,
});

export type ImageHolder = {
  image: Image;
  url: string;
};

export type PropertyHolder = {
  id: number;
  property: Property;
  images: Array<ImageHolder>;
  lease_contracts: Array<LeaseContract>;
  simulation_configs: SimulationConfigWithId[];
  cover_image_url: string;
  user_input_monthly_cashflow_results: Array<MonthlyCashflowResult>;
};

export const PropertyIds = atom<Array<number>>({
  key: "property_ids",
  default: [],
});

// Note: the following state is loaded from API backend.
// DO NOT use this state in your ReactDOM component directly:
// this state holds properties as concatenated single array and thus it causes huge performance impact.
// Instead, use the following `PropertyHoldersState` atom family.
export const FetchedPropertyHolders = atom<Array<PropertyHolder>>({
  key: "initial_property_holders",
  default: [],
});

export const PropertyHoldersState = atomFamily<PropertyHolder, number>({
  key: "property_holders",
  default: selectorFamily({
    key: "property_holders/default",
    get:
      (id) =>
      ({ get }) => {
        const fetched = get(FetchedPropertyHolders);
        const property = fetched.find((e) => {
          return e.id === id;
        });
        return property;
      },
  }),
});

export const SelectedPropertyId = atom<number | null>({
  key: "selected_property_id",
  default: null,
});

export const SelectedPropertyIds = atom<Array<number>>({
  key: "selected_property_ids",
  default: [],
});

export type SellSimulationState = {
  enable: boolean;
  sell_at: string;
  price: number;
};

export type PriceSimulationState = {
  enablePriceSimulationFromProposal: boolean;
  enableBalanceSimulationFromProposal: boolean;
};

export const PropertySellSimulationState = atomFamily<SellSimulationState, number>({
  key: "property_sell_simulation_state",
  default: {
    enable: false,
    sell_at: dayjs().format("YYYY/MM"),
    price: 0,
  },
});

export const DisabledPriceSimulation = {
  enablePriceSimulationFromProposal: false,
  enableBalanceSimulationFromProposal: false,
} as const;

export const PropertyPriceSimulationState = atomFamily<PriceSimulationState, number>({
  key: "property_price_simulation_state",
  default: DisabledPriceSimulation,
});

export const useSelectAllProperties = (): Function => {
  const property_ids = useRecoilValue(PropertyIds);
  const setSelectedPropertyId = useSetRecoilState(SelectedPropertyId);
  const setSelectedPropertyIds = useSetRecoilState(SelectedPropertyIds);
  return () => {
    setSelectedPropertyId(null);
    setSelectedPropertyIds(property_ids);
  };
};

export const useSelectFirstProperty = (): Function => {
  const property_ids = useRecoilValue(PropertyIds);
  const setSelectedPropertyId = useSetRecoilState(SelectedPropertyId);
  const setSelectedPropertyIds = useSetRecoilState(SelectedPropertyIds);
  return () => {
    setSelectedPropertyId(property_ids[0]);
    setSelectedPropertyIds([property_ids[0]]);
  };
};

export const useSelectLimitedProperties = (limit: number): Function => {
  const property_ids = useRecoilValue(PropertyIds);
  const setSelectedPropertyId = useSetRecoilState(SelectedPropertyId);
  const setSelectedPropertyIds = useSetRecoilState(SelectedPropertyIds);
  return () => {
    setSelectedPropertyId(property_ids[0]);
    setSelectedPropertyIds(property_ids.slice(0, limit));
  };
};

export const useSelectOwnedProperties = (): Function => {
  const properties = useRecoilValue(Properties);
  const setSelectedPropertyId = useSetRecoilState(SelectedPropertyId);
  const setSelectedPropertyIds = useSetRecoilState(SelectedPropertyIds);
  return () => {
    setSelectedPropertyId(null);
    setSelectedPropertyIds(
      properties
        .filter((property) => {
          return property.property.property_type === PROPERTY_TYPE_VALUES.owned || property.property.property_type === PROPERTY_TYPE_VALUES.owned_sample;
        })
        .map((p) => {
          return p.id;
        })
    );
  };
};

export const SelectableMinMonth = "1960/01";
export const SelectableMaxMonth = "2149/12";
export const SelectedStartingMonth = atom<Dayjs>({
  key: "SelectedStartingMonth",
  default: dayjs().startOf("year"),
});

export const SelectedEndingMonth = atom<Dayjs>({
  key: "SelectedEndingMonth",
  default: dayjs().endOf("year").add(45, "year"),
});
export const ComparisonTargetMonth = atom<Dayjs>({
  key: "ComparisonTargetMonth",
  default: dayjs(),
});

export const FromPropertyTypes = {
  owned: "owned",
  consideration: "consideration",
} as const;

export const FromPropertyTypeState = atom<keyof typeof FromPropertyTypes | null>({
  key: "FromPropertyTypeState",
  default: null,
});

export const UserRegistrationsFormState = atom<UserRegistrations>({
  key: "UserRegistrationsFormState",
  default: {
    family_name: "",
    first_name: "",
    family_name_kana: "",
    first_name_kana: "",
    email: "",
    phone_number: "",
    birthday: "",
    job_category: JobCategory.その他,
    request_the_proposal: false,
    password: "",
    password_confirmation: "",
    phone_number_auth_code: 0,
  },
});

export const UserRegistrationsAccoutFormState = atom<AccountRegisterFormSchema>({
  key: "UserRegistrationsAccoutFormState",
  default: DEFAULT_ACCOUNT_REGISTER_FORM_SCHEMA,
  effects_UNSTABLE: [
    ({ onSet }) => {
      onSet((newValue: AccountRegisterFormSchema) => {
        // LocalStorageに保存
        setStorageAccountRegistrationState(newValue);
      });
    },
  ],
});

// 物件登録 - 保有物件
export const PropertyRegistrationState = atom<AddNewPropertyFormSchema>({
  key: "PropertyRegistrationState",
  default: DEFAULT_ADD_NEW_PROPERTY_FORM_SCHEMA,
  effects_UNSTABLE: [
    ({ onSet }) => {
      onSet((newValue: AddNewPropertyFormSchema) => {
        // LocalStorageに保存
        setStoragePropertyRegistrationState(newValue);
      });
    },
  ],
});

// 物件登録 - 検討中物件
export const ConsiderationPropertyRegistrationState = atom<AddNewPropertyFormSchema>({
  key: "ConsiderationPropertyRegistrationState",
  default: DEFAULT_ADD_NEW_PROPERTY_FORM_SCHEMA,
  effects_UNSTABLE: [
    ({ onSet }) => {
      onSet((newValue: AddNewPropertyFormSchema) => {
        // LocalStorageに保存
        setStorageConsiderationPropertyRegistrationState(newValue);
      });
    },
  ],
});

// 物件一覧画面のスナックバーに表示するメッセージ
export type ManagementListSnackbar = {
  message: string;
  status: "success" | "error" | "";
  isOpen: boolean;
};

export const PropertyManagementListSnackbarState = atom<ManagementListSnackbar>({
  key: "property_management_list_snackbar_state",
  default: {
    message: "",
    status: "",
    isOpen: false,
  },
});

// 物件詳細画面のスナックバーに表示するメッセージ
export type ManagementDetailSnackbar = {
  message: string;
  status: "success" | "error" | "";
  isOpen: boolean;
};

export const PropertyManagementDetailSnackbarState = atom<ManagementDetailSnackbar>({
  key: "property_management_detail_snackbar_state",
  default: {
    message: "",
    status: "",
    isOpen: false,
  },
});

export const PropertyEditPageTypes = {
  property_info: "property_info", // 物件詳細
  property_info_extra: "property_info_extra", // 物件詳細の単発でかかる経費情報
  purchase_info: "purchase_info", // 購入時情報
  loan_info: "loan_info", // 借入情報
  management_cost: "management_cost", // 賃貸管理費 経費情報
  lease_contract: "lease_contract", // 賃貸契約情報
  insurance_info: "insurance_info", // 保険情報
} as const;

// 物件編集の表示画面切り替え
export const PropertyEditPageState = atom<keyof typeof PropertyEditPageTypes | null>({
  key: "property_edit_page_state",
  default: null,
});
